import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Building a filterable log viewer with Downshift and match-sorter",
  "description": "",
  "published": "2018-07-18T00:00:00.000Z",
  "redirect_from": ["/blog/filterable-log-viewer-downshift-match-sorter/swizec/8512"],
  "categories": "Front End, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`This was a fun little build, but I wish I remembered to turn on my stream. It was kinda nice to just write some code, too 😇`}</p>
    <p><inlineCode parentName="p">{`<LogViewer>`}</inlineCode>{` takes a stream of server logs, or process logs, or any flat text file (let's be honest) and turns it into a searchable, filterable little thing. Type in the input box, find what you're looking for. Matched strings are highlighted so you know what you're doing.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`This was fun. Writeup tomorrow 🤙 `}<a parentName="p" {...{
          "href": "https://t.co/40usRbykxR"
        }}>{`pic.twitter.com/40usRbykxR`}</a></p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/1019501310197030912"
      }}>{`July 18, 2018`}</a></blockquote>
    <p>{`The whole thing fits into 69 lines of beautifully rendered code and 1071 node_modules dependencies. You can `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/log-viewer"
      }}>{`see the code on GitHub`}</a>{` ✌️`}</p>
    <p><a parentName="p" {...{
        "href": "https://dist-thnnqblnuk.now.sh/"
      }}>{`Try `}{`<`}{`LogViewer>`}</a></p>
    <h2 {...{
      "id": "heres-how-it-works-"
    }}>{`Here's how it works 👇`}</h2>
    <p><inlineCode parentName="p">{`<LogViewer>`}</inlineCode>{` is built out of 2 main components:`}</p>
    <ol>
      <li parentName="ol"><inlineCode parentName="li">{`<LogViewer>`}</inlineCode>{` renders a Downshift component and munches a text file into a list of logs`}</li>
      <li parentName="ol"><inlineCode parentName="li">{`<LogRow>`}</inlineCode>{` takes care of individual lines and highlighting matched strings`}</li>
    </ol>
    <h3 {...{
      "id": "logviewer"
    }}>{`LogViewer`}</h3>
    <p>{`We're using `}<inlineCode parentName="p">{`getDerivedStateFromProps`}</inlineCode>{` to take our `}<inlineCode parentName="p">{`logs`}</inlineCode>{`, which is a flat string, and turn it into an array of entries. Right now, that's just splitting by newlines, but we could perform contextual parsing and understand that a single log can span multiple lines.`}</p>
    <p>{`Perhaps the parser function should come from props 🤔`}</p>
    <p>{`The `}<inlineCode parentName="p">{`render`}</inlineCode>{` method takes `}<inlineCode parentName="p">{`logs`}</inlineCode>{` from state and returns a `}<inlineCode parentName="p">{`<Downshift>`}</inlineCode>{` component. Downshift takes care of driving our input field and some other tidbits that are tedious to do ourselves.`}</p>
    <p>{`Inside Downshift's render prop, we use `}<inlineCode parentName="p">{`matchSorter`}</inlineCode>{` to filter logs based on user input. I don't know what string matching algorithm matchSorter uses, but it's really fast.`}</p>
    <p>{`A+ library 👌`}</p>
    <p>{`Finally, we render a `}<inlineCode parentName="p">{`<div>`}</inlineCode>{` with an `}<inlineCode parentName="p">{`input`}</inlineCode>{` field controlled by Downshift, a `}<inlineCode parentName="p">{`p`}</inlineCode>{` with the count of matches, and a `}<inlineCode parentName="p">{`pre`}</inlineCode>{` with all our logs in a loop. We render each log entry with `}<inlineCode parentName="p">{`<LogRow>`}</inlineCode>{`, providing the current `}<inlineCode parentName="p">{`match`}</inlineCode>{` and `}<inlineCode parentName="p">{`log`}</inlineCode>{` to be rendered.`}</p>
    <h3 {...{
      "id": "logrow"
    }}>{`LogRow`}</h3>
    <p>{`The `}<inlineCode parentName="p">{`<LogRow>`}</inlineCode>{` component renders a string of text, potentially highlighting a part of it.`}</p>
    <p>{`We convert the matched string to lower case. Gonna use it to decide which part of the log to highlight.`}</p>
    <p>{`Then we split our log on a regex using the provided match.`}</p>
    <p>{`The result is an array of log fragments like this`}</p>
    <pre><code parentName="pre" {...{}}>{`match = 'cat'
log = 'my cat is grumpy'

chunks --> ['my ', 'cat', ' is grumpy']
`}</code></pre>
    <p>{`When you split on regex and wrap your split point in parenthesis, `}<inlineCode parentName="p">{`()`}</inlineCode>{`, it's preserved in your output. Perfect 👌 for our purposes and pretty fast too.`}</p>
    <p>{`Rendering is a matter of looping through our `}<inlineCode parentName="p">{`chunks`}</inlineCode>{` array and returning either a flat string or the `}<inlineCode parentName="p">{`<Highlighted>`}</inlineCode>{` styled component.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`<Highlighted>`}</inlineCode>{` component is simplistic because I'm no designer.`}</p>
    <pre><code parentName="pre" {...{}}>{`const Highlight = styled.span\`
    color: red;
    background: yellow;
\`;
`}</code></pre>
    <p>{`Red color, yellow background. High contrast.`}</p>
    <h2 {...{
      "id": "useful"
    }}>{`Useful?`}</h2>
    <p>{`That was an MVP. Should I make it better? Should I open source it?`}</p>
    <p>{`Anyone out there who'd use it? 🤨 `}<a parentName="p" {...{
        "href": "http://twitter.com/swizec"
      }}>{`Ping me on Twitter`}</a>{`.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      